import React, { useCallback, useEffect, useState } from 'react';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import { apiService } from '../../components/Config/api.service';
import SecondarySidebar from '../../components/SideBar/SecondarySidebar';
import PrimeImposable from './Form/PrimeImposable';
import PrimeNonImposable from './Form/PrimeNonImposable';
import Avantages from './Form/PrimeAvantages';
import NoteFrais from './Form/NoteFrais';
import { notify } from '../../components/Utils/Notifications';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import DeductionForm from './Form/DeductionForm';
import { FaCoins,FaStickyNote, FaUsers, FaChartLine, FaGift } from 'react-icons/fa';
import { BiCreditCard } from 'react-icons/bi';
import { formatMoney } from '../../components/Utils/FormatUtils';

function Primes() {
    const [filterBy, setFilterBy] = useState('');
    const [activeTab, setActiveTab] = useState("pimp");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 25,
        total_pages: 1,
        total_count: 0
    });

    const [stats, setStats] = useState({
        total_primes: 0,
        total_advantages: 0,
        total_loans: 0,
        total_notes: 0,
        total_employees: 0
    });

    const columns = activeTab === "notes" ? [
        { key: 'employee_name', label: 'Collaborateur' },
        { key: 'name', label: 'Motif' },
        { key: 'date', label: 'Date' },
        { key: 'amount', label: 'Montant', render: (item) => formatMoney(item.amount) }
    ] : activeTab === "pimp" ? [
        { key: 'name', label: 'Name' },
        { 
            key: 'categoryCode', 
            label: 'Type de valeur',
            render: (item) => {
                const categoryMap = {
                    'BRUT': 'Brut',
                    'CBP': 'Brut (Proratisé)',
                    'PN': 'Net',
                    'PANEX': 'Net (Déjà reçu)',
                    'PNP': 'Net (Proratisé)'
                };
                return categoryMap[item.categoryCode] || item.categoryCode;
            }
        },
        { 
            key: 'proratise', 
            label: 'Proratisé',
            render: (item) => {
                const proratiseCategories = ['CBP', 'PNP'];
                return proratiseCategories.includes(item.categoryCode) ? 'Oui' : 'Non';
            }
        },
        { key: 'employee_count', label: 'Collaborateur concernés' },
        { key: 'amount', label: 'Montant Total', render: (item) => formatMoney(item.amount) },
    ] : activeTab === "pnoimp" ? [
        { key: 'name', label: 'Name' },
        { 
            key: 'affichage', 
            label: 'Affichage',
            render: (item) => {
                return item.categoryCode === 'CBNPNI' ? 'Au niveau du brut' : 'Au niveau du net';
            }
        },
        { key: 'employee_count', label: 'Collaborateur concernés' },
        { key: 'amount', label: 'Montant Total', render: (item) => formatMoney(item.amount) },
    ] : [
        { key: 'name', label: 'Name' },
        { 
            key: 'category', 
            label: 'Type de valeur',
            render: (item) => item.categoryCode === 'Brut' ? 'Brut' : 'Net'
        },    
        { key: 'employee_count', label: 'Collaborateur concernés' },
        { key: 'amount', label: 'Montant Total', render: (item) => formatMoney(item.amount) },
    ];


    const newElement = {
        name: "",
        category: "",
        valeur: 0,
        position: 1,
        employees_primes: [],
        apply_selected: false
    }

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [edit, setEdit] = useState(false);
    const [element, setElement] = useState(newElement);

    const fetchPrimes = useCallback(
        (page = 1, search = '', filter = '', category = activeTab) => {
            setLoading(true);

            const offset = (page - 1) * pagination.limit;
            const params = new URLSearchParams();
            params.append('offset', offset);
            params.append('limit', pagination.limit);
            if (search) {
                params.append('search', search);
            }
            if (filter) {
                params.append('filter_by', JSON.stringify(filter));
            }

            let endpoint = '/primes';
            switch (category) {
                case "pimp":
                    params.append('tax', 'imp');
                    break;
                case "pnoimp":
                    params.append('tax', 'noimp');
                    break;
                case "advantages":
                    endpoint = '/advantages'
                    break;
                case "notes":
                    endpoint = '/notes';
                    break;
                case "deduction":
                    endpoint = '/loans';
                    break;
                default:
                    params.append('tax', 'imp');
            }

            apiService.getElement(`${endpoint}?${params.toString()}`)
                .then(response => {
                    const responseData = response.data;
                    if (endpoint === '/primes') {
                        setData(responseData.primes);
                    } else if (endpoint === '/notes') {
                        setData(responseData.notes || []);
                    } else if (endpoint === '/loans') {
                        setData(responseData.loans || []);
                    } else if (endpoint === '/advantages') {
                        setData(responseData.advantages || []);
                    }

                    if (responseData.pagination) {
                        const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;

                        setPagination({
                            ...responseData.pagination,
                            page: currentPage
                        });
                    }
                    setLoading(false);
                })
                .catch(error => {
                    console.error(`There was an error fetching data from ${endpoint}:`, error);
                    setData([]);
                    setPagination({
                        page: 1,
                        limit: 25,
                        total_pages: 1,
                        total_count: 0
                    });
                    setLoading(false);
                });
        },
        [activeTab, pagination.limit]
    );
    
    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;
    
        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        if (newFilterBy !== filterBy) {
            setFilterBy(newFilterBy);
        }
    
        fetchPrimes(page, newSearchTerm, newFilterBy, activeTab);
    };

    const fetchStats = () => {
        apiService.getElement('/primes/stats')
            .then(response => {
                const responseData = response.data;
                if (responseData.stats) {
                    setStats({
                        total_primes: responseData.stats.total_primes_amount || 0,
                        total_advantages: responseData.stats.total_advantages_amount || 0,
                        total_loans: responseData.stats.total_loans_amount || 0,
                        total_notes: responseData.stats.total_expense_notes_amount || 0,
                        total_employees: responseData.stats.total_employees || 0
                    });
                }
            })
            .catch(error => {
                console.error("There was an error fetching stats:", error);
            });
    };


    useEffect(() => {
        !edit && fetchPrimes(1, searchTerm, filterBy, activeTab);
        fetchStats();
    }, [activeTab, edit, filterBy, searchTerm,  fetchPrimes]);

    



    const menuItems = [
        { id: "pimp", label: "Prime Imposable" },
        { id: "pnoimp", label: "Prime Non Imposable" },
        { id: "advantages", label: "Avantages en natures" },        
        { id: "deduction", label: "Déductions sur salaire" },
        { id: "notes", label: "Note de Frais" },
    ];


    const changeTab = (tab) => {
        if (edit) {
            notify('Veillez enregister les changements ou Annuler', '', 4)
        } else {
            closeEdit();
            setActiveTab(tab);
        }
    }


    const closeEdit = () => {
        setEdit(false);
        setElement(newElement);
    }

    const editPrime = (element = newElement) => {
        setElement(element)
        setEdit(true);
    }


    const dataRendered = () => {
        switch (activeTab) {
            case "pimp":
                return data;
            case "pnoimp":
                return data;
            case "advantages":
                return data;
            case "notes":
                return data;
            case "deduction":
                return data; 
            default:
                return data;
        }
    };



    const renderContent = () => {
        switch (activeTab) {
            case "pnoimp":
                return (
                    <PrimeNonImposable
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                    />
                );
            case "advantages":
                return <Avantages 
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                        />
            case "notes":
                return <NoteFrais 
                        id={element?.id}
                        note={element}
                        setNote={setElement}
                        setErrorMessage={setErrorMessage}
                        setSuccessMessage={setSuccessMessage}
                        closeEdit={closeEdit}
                    />;
            case "deduction":
                return (
                    <DeductionForm
                        id={element?.id}
                        deduction={element}
                        setDeduction={setElement}
                        closeEdit={closeEdit}
                    />
                )

            default:
                return (
                    <PrimeImposable
                        id={element?.id}
                        prime={element}
                        setPrime={setElement}
                        setErrorMessage={setErrorMessage}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}
                        errorMessage={errorMessage}
                        closeEdit={closeEdit}
                    />
                );
        }
    };
    const formatNumber = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(value);
    };

    // Détermine la valeur du model selon activeTab
    const getModel = () => {
        if (activeTab === "pimp" || activeTab === "pnoimp") {
            return "prime";
        }
        return activeTab;
    };

    const getHeaderContent = () => {
        switch (activeTab) {
            case "pimp":
                return {
                    title: "Gestion des Primes Imposables",
                    text: "Gérez les primes imposables de votre entreprise en toute simplicité.",
                    ajout: "Prime "
                };
            case "pnoimp":
                return {
                    title: "Gestion des Primes Non Imposables",
                    text: "Gérez les primes non imposables de votre entreprise.",
                    ajout: "Prime "
                };
            case "advantages":
                return {
                    title: "Gestion des Avantages en Nature",
                    text: "Gérez les avantages en nature attribués à vos collaborateurs.",
                    ajout: "Avantage"
                };
            case "notes":
                return {
                    title: "Gestion des Notes de Frais",
                    text: "Gérez les notes de frais de vos collaborateurs.",
                    ajout: "Note de Frais"
                };
            case "deduction":
                return {
                    title: "Gestion des Déductions sur Salaire",
                    text: "Gérez les déductions et prêts sur salaire.",
                    ajout: "Déduction"
                };
            default:
                return {
                    title: "Gestion des Primes et Avantages",
                    text: "Gérez les Primes de votre entreprise en toute simplicité.",
                    ajout: "Prime"
                };
        }
    };

   const getStatsIcon = (type) => {
        switch (type) {
            case 'primes': return <FaCoins className="text-white" size={24} />;
            case 'advantages': return <FaGift className="text-white" size={24} />;
            case 'deductions': return <BiCreditCard className="text-white" size={24} />;
            case 'notes': return <FaStickyNote className="text-white" size={24} />;
            case 'employees': return <FaUsers className="text-white" size={24} />;
            default: return <FaChartLine className="text-white" size={24} />;
        }
    };

    return (
        <div>
            <HeaderCard
                title="Primes et Avantages"
                text="Configurer les primes, avantages et déductions."
            />
            {/* Statistiques - Restent en haut sur toute la largeur */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { 
                        title: "Primes", 
                        value: formatNumber(stats.total_primes),
                        type: 'primes',
                        subtitle: "Montant total des primes"
                    },
                    { 
                        title: "Avantages", 
                        value: formatNumber(stats.total_advantages),
                        type: 'advantages',
                        subtitle: "Montant total des avantages"
                    },
                    { 
                        title: "Déductions", 
                        value: formatNumber(stats.total_loans),
                        type: 'deductions',
                        subtitle: "Montant total des retenues effectuées"
                    },
                    { 
                        title: "Notes de frais", 
                        value: formatNumber(stats.total_notes),
                        type: 'notes',
                        subtitle: "Montant total des frais remboursés"
                    }
                ].map(({ title, value, type, subtitle }) => (
                    <div
                        key={title}
                        className={`bg-gradient-to-r bg-white rounded-lg p-6    hover:shadow-md transition-shadow duration-300`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <p className=" text-sm font-medium uppercase tracking-wide">
                                    {title}
                                </p>
                                <p className="text-xl font-bold mt-2">{value}</p>
                                <p className="text-xs mt-1">{subtitle}</p>
                            </div>
                            <div className="bg-[--color-primary] rounded-full p-3">
                                {getStatsIcon(type)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex gap-4 py-4 ">
                {/* Sidebar */}
                <div className="w-80 flex-shrink-0 self-start sticky top-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">                                
                                <h3 className="text-lg font-semibold text-gray-800">Types de rémunération</h3>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">Gérez les différents éléments de paie</p>
                        </div>
                        <div className="p-2">
                            <SecondarySidebar 
                                activeTab={activeTab} 
                                setActiveTab={changeTab} 
                                menuItems={menuItems} 
                            />
                        </div>
                    </div>
                </div>
                <div className=" bg-white rounded-lg shadow-md flex-1 min-w-0">
                    {loading && <LoadingBarWithStyles />}
                    <div className='p-8'>
                        <div className="container mx-auto ">
                            <div className="">
                                {!edit && (
                                    <HeaderCard
                                        title={getHeaderContent().title}
                                        text={getHeaderContent().text}
                                        ajout={getHeaderContent().ajout}
                                        addNew={editPrime}
                                    />
                                )}
                            </div>
                            <div className="">
                                {edit ?
                                    renderContent() :
                                    <TableList
                                        columns={columns}
                                        data={dataRendered()}
                                        onRowClick={editPrime}
                                        pagination={pagination}
                                        onPageChange={handlePageChange}
                                        searchQuery={searchTerm}
                                        setSearchQuery={setSearchTerm}
                                        // setFilterBy={setFilterBy}
                                        // filterBy={filterBy}
                                        showFilter={false}
                                        model={getModel()}
                                        loading={loading}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Primes;